import type {
  CertFilters,
  CertKpiFilterKey,
  CertificateStore,
  CertSortConfig,
  CertStoreStatus,
  UtmCertificate,
} from './types'
import { compareNaturalText } from '@/shared/formatters'

export function parseRuCertDate(dateStr: unknown): Date | null {
  if (!dateStr || dateStr === 'Неизвестно' || dateStr === 'Ошибка' || dateStr === 'Не указан IP') {
    return null
  }
  const [datePart, timePart] = String(dateStr).split(', ')
  if (!datePart || !timePart) return null
  const [day, month, year] = datePart.split('.').map(Number)
  const [hours, minutes] = timePart.split(':').map(Number)
  if (!day || !month || !year || Number.isNaN(hours) || Number.isNaN(minutes)) return null
  return new Date(year, month - 1, day, hours, minutes)
}

export function formatDateCompact(dateStr: unknown): string {
  if (!dateStr || dateStr === 'Неизвестно' || dateStr === 'Ошибка' || dateStr === 'Не указан IP') {
    return String(dateStr || '—')
  }
  const [datePart] = String(dateStr).split(', ')
  if (!datePart) return String(dateStr)
  const parts = datePart.split('.')
  if (parts.length >= 3) {
    const [day, month, year] = parts
    return `${day}.${month}.${String(year).slice(-2)}`
  }
  return datePart
}

export function getCertByType(store: CertificateStore, type: 'RSA' | 'GOST'): UtmCertificate | undefined {
  return (store.certificates || []).find((c) => c.certType === type)
}

export function getStoreStatus(store: CertificateStore): CertStoreStatus {
  const certs = store.certificates || []
  const rsa = getCertByType(store, 'RSA')
  const gost = getCertByType(store, 'GOST')
  const hasError = [rsa, gost].some(
    (c) => c && (c.startDate === 'Ошибка' || c.expireDate === 'Ошибка'),
  )
  const hasNoIP = [rsa, gost].some(
    (c) =>
      c &&
      (c.startDate === 'Не указан IP' ||
        c.expireDate === 'Не указан IP' ||
        c.startDate === 'Не указан УТМ' ||
        c.expireDate === 'Не указан УТМ'),
  )
  const hasExpiring = certs.some((c) => c.isExpiringSoon)
  if (hasError) return 'error'
  if (hasNoIP) return 'no-ip'
  if (hasExpiring) return 'expiring'
  return 'active'
}

export function getStatusInfo(status: CertStoreStatus) {
  switch (status) {
    case 'error':
      return { text: 'Ошибка', tone: 'bad' as const }
    case 'no-ip':
      return { text: 'Нет IP', tone: 'muted' as const }
    case 'expiring':
      return { text: 'Скоро истекает', tone: 'warn' as const }
    default:
      return { text: 'Активен', tone: 'active' as const }
  }
}

export function getCertCellTone(cert: UtmCertificate | undefined): 'muted' | 'err' | 'warn' | 'ok' {
  if (!cert) return 'muted'
  if (cert.startDate === 'Ошибка' || cert.expireDate === 'Ошибка') return 'err'
  if (cert.startDate === 'Не указан IP' || cert.startDate === 'Не указан УТМ') return 'muted'
  if (cert.isExpiringSoon) return 'warn'
  return 'ok'
}

export function applyCertFilters(data: CertificateStore[], filters: CertFilters): CertificateStore[] {
  const s = (filters.search || '').toLowerCase().trim()
  const statusF = filters.status || ''

  return data.filter((store) => {
    const name = (store.storeName || '').toLowerCase()
    const idStr = String(store.storeId ?? '')
    const matchesSearch = !s || name.includes(s) || idStr.includes(s)
    const st = getStoreStatus(store)
    if (!statusF) return matchesSearch
    if (statusF === 'attention') return matchesSearch && st !== 'active'
    return matchesSearch && st === statusF
  })
}

export function sortCertData(data: CertificateStore[], sort: CertSortConfig): CertificateStore[] {
  const { key, direction } = sort
  return [...data].sort((a, b) => {
    if (key === 'storeName') {
      const byName = compareNaturalText(a.storeName, b.storeName)
      const result = byName || a.storeId - b.storeId
      return direction === 'asc' ? result : -result
    }

    let valueA: string | number | Date | null
    let valueB: string | number | Date | null

    if (key === 'rsaExpire' || key === 'gostExpire') {
      const certType = key === 'rsaExpire' ? 'RSA' : 'GOST'
      const certA = getCertByType(a, certType)
      const certB = getCertByType(b, certType)
      valueA = certA ? parseRuCertDate(certA.expireDate) : null
      valueB = certB ? parseRuCertDate(certB.expireDate) : null
    } else if (key === 'status') {
      const order: Record<CertStoreStatus, number> = { error: 0, expiring: 1, 'no-ip': 2, active: 3 }
      valueA = order[getStoreStatus(a)]
      valueB = order[getStoreStatus(b)]
    } else {
      valueA = ''
      valueB = ''
    }

    if (key === 'rsaExpire' || key === 'gostExpire') {
      if (valueA === null && valueB === null) return 0
      if (valueA === null) return 1
      if (valueB === null) return -1
    }

    if (valueA! < valueB!) return direction === 'asc' ? -1 : 1
    if (valueA! > valueB!) return direction === 'asc' ? 1 : -1
    return 0
  })
}

export function computeCertKpi(data: CertificateStore[]) {
  let active = 0
  let expiring = 0
  let error = 0
  let noIp = 0
  for (const store of data) {
    const st = getStoreStatus(store)
    if (st === 'active') active++
    else if (st === 'expiring') expiring++
    else if (st === 'error') error++
    else if (st === 'no-ip') noIp++
  }
  return {
    total: data.length,
    active,
    expiring,
    error,
    noIp,
    attention: data.filter((s) => getStoreStatus(s) !== 'active').length,
  }
}

export function filtersFromCertKpi(key: CertKpiFilterKey): Partial<CertFilters> {
  switch (key) {
    case 'all':
      return { search: '', status: '' }
    case 'attention':
      return { status: 'attention' }
    default:
      return { status: key }
  }
}
